import {
  AddressRequest,
  BuildingConditionValueKeys,
  EnergyClassKeys,
  PostGenerateIndexChartDataRequest,
  ValuationRentKeys,
} from "@/api/sprengConnectApi";
import { Category } from "@/api/sprengConnectApi/types/Category";
import {
  EnergyEfficiencyGroup,
  ExpenseType,
  LocationResolutionFilterData,
  LocationResolutionType,
  ObjectState,
  ObjectType,
  PlaygroundFilter,
  TimeResolution,
} from "@/types";
import { IndexType } from "@/types/IndexType";

import { getObjectTypeForIndex } from "./playgroundFilterHelper";

const getAddressRequests = (locationResolution: LocationResolutionFilterData): AddressRequest[] => {
  let addressData: AddressRequest[] = [];
  if (
    locationResolution.type === LocationResolutionType.PostalCode &&
    locationResolution.metadata.postalCodes != null
  ) {
    addressData = locationResolution.metadata.postalCodes.map((postalCode) => ({ zip: postalCode }));
  }

  return addressData;
};

export const mapObjectTypeToCategory = (objectType: ObjectType): Category => {
  switch (objectType) {
    case ObjectType.OneFamilyDwelling:
      return Category.EFH;
    case ObjectType.MultiFamilyDwelling:
      return Category.MFH;
    case ObjectType.OwnerOccupiedDwelling:
      return Category.ETW;
  }
};

export const responseToObjectTypeMapping: { [key: string]: ObjectType } = {
  ETW: ObjectType.OwnerOccupiedDwelling,
  EFH: ObjectType.OneFamilyDwelling,
  MFH: ObjectType.MultiFamilyDwelling,
};

export const mapResponseToObjectType = (responseValue: string): ObjectType => {
  return responseToObjectTypeMapping[responseValue];
};

export const mapEnergyEfficiencyGroupToEnergyClass = (
  energyEfficiencyGroup?: EnergyEfficiencyGroup,
): EnergyClassKeys | undefined => {
  switch (energyEfficiencyGroup) {
    case EnergyEfficiencyGroup.High:
      return "top";
    case EnergyEfficiencyGroup.Medium:
      return "medium";
    case EnergyEfficiencyGroup.Low:
      return "low";
    default:
      return undefined;
  }
};

export const mapEnergyClassToEnergyEfficiencyGroup = (
  energyClass: EnergyClassKeys | undefined,
): EnergyEfficiencyGroup | undefined => {
  switch (energyClass) {
    case "top":
      return EnergyEfficiencyGroup.High;
    case "medium":
      return EnergyEfficiencyGroup.Medium;
    case "low":
      return EnergyEfficiencyGroup.Low;
    default:
      return undefined;
  }
};

export const mapValuationOrRentToExpenseType = (valuationOrRent?: ValuationRentKeys): ExpenseType | undefined => {
  switch (valuationOrRent) {
    case "valuation":
      return ExpenseType.Buy;
    case "rent":
      return ExpenseType.Rent;
  }
};

export const mapExpenseTypeToValuationOrRent = (expenseType?: ExpenseType): ValuationRentKeys | undefined => {
  switch (expenseType) {
    case ExpenseType.Buy:
      return "valuation";
    case ExpenseType.Rent:
      return "rent";
  }
};

export const mapBuildingConditionToObjectStates = (
  buildingCondition?: BuildingConditionValueKeys,
): ObjectState | undefined => {
  switch (buildingCondition) {
    case "value_new":
      return ObjectState.NewBuilding;
    case "value_existing":
      return ObjectState.ExistingBuilding;
  }
};

export const mapObjectStateToBuildingCondition = (
  objectState?: ObjectState,
): BuildingConditionValueKeys | undefined => {
  switch (objectState) {
    case ObjectState.NewBuilding:
      return "value_new";
    case ObjectState.ExistingBuilding:
      return "value_existing";
  }
};

enum RequestFilterType {
  address = "address",
  category = "category",
  timeResolution = "timeResolution",
  expenseType = "expenseType",
  quarterly = "quarterly",
  areaType = "area_type",
  scale = "scale",
}

export const mapFilterToPostGenerateIndexChartDataRequests = (
  { objectTypes, locationResolution, timeResolution, expenseType, areaType }: PlaygroundFilter,
  index: IndexType,
  fromYear?: number,
  toYear?: number,
  isGermany?: boolean,
): PostGenerateIndexChartDataRequest[] => {
  const chosenObjectTypes = objectTypes ?? [];
  const addressRequests = getAddressRequests(locationResolution);

  const requiresTimeResolution = index === IndexType.SIM;

  const properties: Record<RequestFilterType, unknown[] | undefined> = {
    [RequestFilterType.category]: getObjectTypeForIndex(index, IndexType.SIPI ? expenseType : undefined).filter(
      (availableObjectType) => chosenObjectTypes.includes(availableObjectType),
    ),
    [RequestFilterType.address]: isGermany ? [{ nation: "DE" }] : addressRequests,
    [RequestFilterType.timeResolution]: timeResolution && requiresTimeResolution ? [timeResolution] : undefined,
    [RequestFilterType.quarterly]:
      (index === IndexType.SIM || index === IndexType.SIPI) && timeResolution ? [timeResolution] : undefined,
    [RequestFilterType.areaType]: index === IndexType.SEPI && isGermany ? [areaType] : undefined,
    [RequestFilterType.scale]: isGermany ? ["COUNTRY"] : undefined,
    [RequestFilterType.expenseType]:
      index === IndexType.SIPI && expenseType ? [mapExpenseTypeToValuationOrRent(expenseType)] : undefined,
  };

  const propertiesWithValues = Object.entries(properties).filter(
    (entry): entry is [key: RequestFilterType, value: unknown[]] => entry[1] != undefined && entry[1].length > 0,
  );

  const requests = propertiesWithValues.reduce((acc, [key, values]) => {
    const newCombinations: Partial<PostGenerateIndexChartDataRequest>[] = [];
    acc.forEach((combination) => {
      values.forEach((value) => {
        let valueToUse = value;
        if (key === RequestFilterType.category) {
          valueToUse = mapObjectTypeToCategory(value as ObjectType);
        }
        if (key === RequestFilterType.quarterly) {
          valueToUse = value === TimeResolution.Quarterly;
        }
        newCombinations.push({
          ...combination,
          [key]: valueToUse,
          index: index,
          fromYear,
          toYear,
        });
      });
    });

    return newCombinations;
  }, new Array<Partial<PostGenerateIndexChartDataRequest>>({}));

  return requests.filter(
    (request): request is PostGenerateIndexChartDataRequest => request.address != null && request.category != null,
  );
};
